// Pacotes de leads — aba de Configurações (super admin). Clone do
// SmsPacotesManager com as rotas /leads/admin/pacotes.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS } from "../../styles/aerosTokens";
import { formatBRL, formatMilhar, formatPrecoUnitario, precoPorLead } from "../../pages/Leads/leadsRules";

const GRID = "minmax(0,1.4fr) 110px 120px 92px 78px";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    root: { display: "flex", flexDirection: "column", gap: 16 },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      minWidth: 0,
    },
    cardPad: { padding: "16px 18px" },
    title: {
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.primary,
      marginBottom: 12,
    },
    form: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.4fr) 120px 120px 120px",
      gap: 10,
      alignItems: "end",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    rowHead: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 8,
      padding: "10px 16px",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      borderBottom: `1px solid ${border}`,
    },
    row: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 8,
      padding: "11px 16px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.04)"
      }`,
      alignItems: "center",
      fontSize: 12.5,
    },
    // Pacote inativo esmaecido, como no mockup: continua no histórico mas não
    // aparece para as empresas comprarem.
    rowOff: { opacity: 0.45 },
    nome: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    num: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    numMuted: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.secondary,
    },
    unit: {
      textAlign: "right",
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
    },
    switchCell: { display: "flex", justifyContent: "center" },
    nota: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "12px 16px",
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 15, flex: "none" },
    },
    vazio: {
      padding: "20px 16px",
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },
    botao: {
      borderRadius: 9,
      fontWeight: 700,
      fontSize: 12.5,
      textTransform: "none",
      background: `linear-gradient(135deg, ${AEROS.defaultPrimary}, ${AEROS.cyan})`,
      color: "#07080D",
      padding: "9px 12px",
      "&.Mui-disabled": { opacity: 0.5, color: "#07080D" },
    },
  };
});

const VAZIO = { nome: "", quantidade: "", valor: "" };

const LeadsPacotesManager = () => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [pacotes, setPacotes] = useState([]);
  const [form, setForm] = useState(VAZIO);
  const [criando, setCriando] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const carrega = useCallback(async () => {
    try { const { data } = await api.get("/leads/admin/pacotes"); setPacotes(data || []); } catch (err) { toastError(err); }
  }, []);
  useEffect(() => { carrega(); }, [carrega]);

  const handleCriar = async () => {
    setCriando(true);
    try {
      await api.post("/leads/admin/pacotes", { nome: form.nome, quantidade: Number(form.quantidade), valor: Number(form.valor) });
      toast.success(i18n.t("aeros.leadsAdmin.created")); setForm(VAZIO); await carrega();
    } catch (err) { toastError(err); }
    setCriando(false);
  };
  const handleToggleAtivo = async (p) => {
    setTogglingId(p.id);
    try { await api.put(`/leads/admin/pacotes/${p.id}`, { nome: p.nome, quantidade: p.quantidade, valor: Number(p.valor), ativo: !p.ativo }); await carrega(); } catch (err) { toastError(err); }
    setTogglingId(null);
  };
  const podeCriar = form.nome.trim() && Number(form.quantidade) > 0 && Number(form.valor) > 0;

  return (
    <div className={classes.root}>
      <div className={`${classes.card} ${classes.cardPad}`}>
        <div className={classes.title}>{i18n.t("aeros.leadsAdmin.newPack")}</div>
        <div className={classes.form}>
          <div><label className={m.fieldLabel}>{i18n.t("aeros.leadsAdmin.name")}</label>
            <TextField fullWidth variant="outlined" className={`${m.input} ${m.inputSmall}`} placeholder={i18n.t("aeros.leadsAdmin.namePlaceholder")} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
          <div><label className={m.fieldLabel}>{i18n.t("aeros.leadsAdmin.quantity")}</label>
            <TextField fullWidth type="number" variant="outlined" className={`${m.input} ${m.inputSmall}`} value={form.quantidade} onChange={(e) => setForm({ ...form, quantidade: e.target.value })} inputProps={{ min: 1 }} /></div>
          <div><label className={m.fieldLabel}>{i18n.t("aeros.leadsAdmin.value")}</label>
            <TextField fullWidth type="number" variant="outlined" className={`${m.input} ${m.inputSmall}`} value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} inputProps={{ min: 0, step: "0.01" }} /></div>
          <Button className={classes.botao} onClick={handleCriar} disabled={criando || !podeCriar}>{i18n.t("aeros.leadsAdmin.create")}</Button>
        </div>
      </div>
      <div className={classes.card}>
        <div className={classes.rowHead}>
          <div>{i18n.t("aeros.leadsAdmin.name")}</div>
          <div style={{ textAlign: "right" }}>{i18n.t("aeros.leadsAdmin.quantity")}</div>
          <div style={{ textAlign: "right" }}>{i18n.t("aeros.leadsAdmin.value")}</div>
          <div style={{ textAlign: "right" }}>{i18n.t("aeros.leadsAdmin.perLead")}</div>
          <div style={{ textAlign: "center" }}>{i18n.t("aeros.leadsAdmin.active")}</div>
        </div>
        {pacotes.length === 0 ? <div className={classes.vazio}>{i18n.t("aeros.leadsAdmin.packsEmpty")}</div> : pacotes.map((p) => (
          <div className={`${classes.row} ${p.ativo ? "" : classes.rowOff}`} key={p.id}>
            <div className={classes.nome} title={p.nome}>{p.nome}</div>
            <div className={classes.num}>{formatMilhar(p.quantidade)}</div>
            <div className={classes.numMuted}>{formatBRL(p.valor)}</div>
            <div className={classes.unit}>{formatPrecoUnitario(precoPorLead(p)) || "—"}</div>
            <div className={classes.switchCell}><Switch checked={Boolean(p.ativo)} onChange={() => handleToggleAtivo(p)} color="primary" disabled={togglingId === p.id} inputProps={{ "aria-label": i18n.t("aeros.leadsAdmin.active") }} /></div>
          </div>
        ))}
        <div className={classes.nota}><VisibilityOffIcon />{i18n.t("aeros.smsPacks.inactiveNote")}</div>
      </div>
    </div>
  );
};

export default LeadsPacotesManager;
